import {
  Card,
  DeleteIcon,
  GKButton,
  GKClientDataGrid,
  Grid2,
  IconButton,
  KebabMenu2Icon,
  PlusIcon,
  Popover,
  Show,
  StarFillIcon,
  StarIcon,
  Typography,
} from "components";
import { EquitybasketFormInterface } from "interfaces/EquityBasket";
import React from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { moneyCommaSeparator } from "utils";
import { handleCreateFormData } from "utils/formHelpers";
import EquityBasketFeePlanSelection from "../EquityBasketFeePlanSelection";

const FeePlansTab = ({
  handleSetFeeDefault,
  handleUpdateEquityBasket,
  updateLoading,
  openFeePlanModal,
  setOpenFeePlanModal,
  feePlanHistoryData,
}: {
  handleSetFeeDefault: any;
  handleUpdateEquityBasket: any;
  updateLoading: any;
  openFeePlanModal: any;
  feePlanHistoryData: {
    id: number;
    name: string;
    startDate: string;
    endDate: string;
  }[];
  setOpenFeePlanModal: any;
}) => {
  const { id } = useParams();

  const form = useFormContext<EquitybasketFormInterface>();

  const [selectedFeePlan, setSelectedFeePlan] = React.useState<any>(null);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Grid2 container spacing={2}>
        <Grid2
          bgcolor={"#F8F8F8"}
          p={3}
          borderRadius={"6px"}
          size={12}
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          {form?.getValues("feePlansList")?.map((item, index) => {
            return (
              <Card
                sx={{ p: 1, minWidth: "250px", maxWidth: "250px" }}
                key={index}
              >
                <Grid2 container spacing={1}>
                  <Grid2 size={8} display={"flex"} alignItems={"center"}>
                    <Typography
                      fontWeight={600}
                      fontSize={16}
                      whiteSpace=" nowrap"
                      overflow=" hidden"
                      textOverflow=" ellipsis"
                    >
                      {item.name || "-"}
                    </Typography>
                  </Grid2>
                  <Grid2
                    size={4}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"flex-end"}
                  >
                    <IconButton
                      onClick={() => {
                        handleSetFeeDefault(item?.id);
                        handleUpdateEquityBasket(
                          handleCreateFormData({
                            values: form.getValues(),
                            dirtyFields: form.formState.dirtyFields,
                          }),
                        );
                      }}
                    >
                      {item?.isDefault ? (
                        <StarFillIcon color="#E6B532" height={20} width={20} />
                      ) : (
                        <StarIcon height={20} width={20} />
                      )}
                    </IconButton>
                    <Show.When isTrue={!item?.isDefault}>
                      <IconButton
                        onClick={(event) => {
                          handleClick(event);
                          setSelectedFeePlan(item);
                        }}
                      >
                        <KebabMenu2Icon />
                      </IconButton>
                    </Show.When>
                  </Grid2>
                  <Show.When
                    isTrue={item.managementFee?.fee || item.performanceFee?.fee}
                  >
                    <Grid2
                      size={5}
                      display={"flex"}
                      justifyContent={"center"}
                      flexDirection={"column"}
                    >
                      <Typography
                        textTransform={"capitalize"}
                        color={"var(--neutral-500)"}
                        fontSize={12}
                      >
                        {item.managementFee?.name || "Management Fee"}
                      </Typography>
                      <Typography fontWeight={600}>
                        {item.managementFee?.feeType === "Flat Fee"
                          ? moneyCommaSeparator(item.managementFee?.fee || 0)
                          : `${item.managementFee?.fee || 0}%` || "-"}
                      </Typography>
                    </Grid2>
                    <Grid2
                      size={2}
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"center"}
                    >
                      <div
                        style={{
                          borderRadius: 4,
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          padding: 4,
                        }}
                      >
                        <PlusIcon color={"var(--neutral-500)"} />
                      </div>
                    </Grid2>
                    <Grid2
                      size={5}
                      display={"flex"}
                      justifyContent={"center"}
                      flexDirection={"column"}
                    >
                      <Typography
                        textTransform={"capitalize"}
                        color={"var(--neutral-500)"}
                        fontSize={12}
                      >
                        {item.performanceFee?.name || "Performance Fee"}
                      </Typography>
                      <Typography fontWeight={600}>
                        {`${item.performanceFee?.fee || 0}%` || "-"}
                      </Typography>
                    </Grid2>
                  </Show.When>
                  <Show.When
                    isTrue={
                      !item.managementFee?.fee && !item.performanceFee?.fee
                    }
                  >
                    <Grid2 size={12}>
                      <Typography fontWeight={600} color={"var(--neutral-500)"}>
                        Subscription Fee
                      </Typography>
                    </Grid2>
                  </Show.When>
                </Grid2>
              </Card>
            );
          })}
        </Grid2>
        <Grid2 size={12}>
          <GKClientDataGrid
            columns={[
              {
                field: "name",
                flex: 1,
                disableColumnMenu: true,
              },
              {
                field: "startDate",
                headerName: "Start Date",
                flex: 1,
                disableColumnMenu: true,
              },
              {
                field: "endDate",
                headerName: "End Date",
                flex: 1,
                disableColumnMenu: true,
              },
            ]}
            rows={feePlanHistoryData || []}
            name="equityBasketFeePlan"
          />
        </Grid2>
      </Grid2>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <GKButton
          startIcon={<DeleteIcon />}
          color="error"
          onClick={() => {
            const newFeePlans = form
              ?.getValues("feePlans")
              ?.filter((filterData) => +selectedFeePlan?.id !== +filterData);
            const newFeePlansList = form
              ?.getValues("feePlansList")
              ?.filter(
                (filterData) => +selectedFeePlan?.id !== +filterData?.id,
              );
            form.setValue("feePlans", newFeePlans);
            form.setValue("feePlansList", newFeePlansList);
          }}
        >
          Delete Plan
        </GKButton>
      </Popover>
      {openFeePlanModal && (
        <EquityBasketFeePlanSelection
          open={openFeePlanModal}
          setOpen={setOpenFeePlanModal}
          updateLoading={updateLoading}
          handleUpdateEquityBasket={handleUpdateEquityBasket}
        />
      )}
    </>
  );
};

export default FeePlansTab;
